import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, AlertCircle, Clock, CheckCircle, XCircle, Users, X, Settings as SettingsIcon, MessageSquare } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { settingsService } from "@/services/settingsService";
import { useToast } from "@/components/ui/use-toast";

type FilterStatus = 'all' | 'open' | 'in_progress' | 'waiting_customer' | 'resolved' | 'closed';
type FilterPriority = 'all' | 'low' | 'medium' | 'high' | 'urgent';
type FilterCategory = 'all' | 'bug' | 'feature_request' | 'question' | 'billing' | 'technical' | 'other';

interface TicketFiltersProps {
  onSearchChange: (value: string) => void;
  onStatusChange: (value: FilterStatus) => void;
  onPriorityChange: (value: FilterPriority) => void;
  onCategoryChange: (value: FilterCategory) => void;
  onUnreadChange: (value: boolean) => void;
  statusValue: FilterStatus;
  priorityValue: FilterPriority;
  categoryValue: FilterCategory;
  unreadValue: boolean;
}

export function TicketFilters({
  onSearchChange,
  onStatusChange,
  onPriorityChange,
  onCategoryChange,
  onUnreadChange,
  statusValue,
  priorityValue,
  categoryValue,
  unreadValue,
}: TicketFiltersProps) {
  const { toast } = useToast();
  // Dropdown açık/kapalı durumu
  const [open, setOpen] = useState(false);
  // Settings modal
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [liveChatEnabled, setLiveChatEnabled] = useState(true);
  const [, setSettingsLoading] = useState(false);

  // Ayarları yükle
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await settingsService.getSettings();
        setLiveChatEnabled(settings.liveChatEnabled);
      } catch (error) {
        console.error('Ayarlar yüklenemedi:', error);
      }
    };
    loadSettings();
  }, []);

  // Anlık chat toggle
  const handleLiveChatToggle = async (enabled: boolean) => {
    const previousValue = liveChatEnabled;
    setLiveChatEnabled(enabled); // Hemen güncelle (optimistic)
    setSettingsLoading(true);
    try {
      await settingsService.updateSettings({ liveChatEnabled: enabled });
      toast({
        title: 'Ayar Güncellendi',
        description: enabled
          ? 'Anlık mesajlaşma açıldı. Müşteriler ticket içinde mesaj yazabilir.'
          : 'Anlık mesajlaşma kapatıldı. Müşteriler sadece ticket oluşturabilir.',
      });
    } catch (error) {
      console.error('Ayar güncellenemedi:', error);
      setLiveChatEnabled(previousValue); // Hata olursa geri al
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Ayar güncellenirken bir hata oluştu.',
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  // Filtreleri sıfırla ve menüyü kapat
  const resetFilters = () => {
    onStatusChange('all');
    onPriorityChange('all');
    onCategoryChange('all');
    onUnreadChange(false);
    setOpen(false);
  };

  // Herhangi bir filtre aktif mi?
  const isAnyFilterActive =
    statusValue !== 'all' ||
    priorityValue !== 'all' ||
    categoryValue !== 'all' ||
    unreadValue;

  // Tıklama işleyicileri - değerleri değiştir ve menüyü kapat
  const handleStatusChange = (value: FilterStatus) => {
    onStatusChange(value);
    onPriorityChange('all');
    onCategoryChange('all');
    onUnreadChange(false);
    setOpen(false);
  };

  const handlePriorityChange = (value: FilterPriority) => {
    onStatusChange('all');
    onPriorityChange(value);
    onCategoryChange('all');
    onUnreadChange(false);
    setOpen(false);
  };

  const handleUnreadChange = (value: boolean) => {
    if (value) {
      onStatusChange('all');
      onPriorityChange('all');
      onCategoryChange('all');
    }
    onUnreadChange(value);
    setOpen(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <div className="space-y-4">
        {/* Arama ve Filtre */}
        <div className="flex gap-3 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Ticket no, konu, müşteri adı veya email ile arayın..."
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-12 h-11 border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className={`h-11 px-5 rounded-lg border-slate-200 hover:bg-slate-50 ${isAnyFilterActive ? "bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100" : ""}`}
              >
                <Filter className="mr-2 h-4 w-4" />
                Filtrele
                {isAnyFilterActive && (
                  <span className="ml-2 bg-emerald-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {[statusValue !== 'all', priorityValue !== 'all', categoryValue !== 'all', unreadValue].filter(Boolean).length}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 p-2">
              {/* Durum Filtreleri */}
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Ticket Durumu
              </div>
              <DropdownMenuItem
                onClick={() => handleStatusChange(statusValue === 'open' ? 'all' : 'open')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${statusValue === 'open' ? 'bg-green-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <AlertCircle className={`mr-3 h-4 w-4 ${statusValue === 'open' ? 'text-green-600' : 'text-slate-400'}`} />
                  <span className={statusValue === 'open' ? 'font-medium text-green-700' : 'text-slate-700'}>Açık</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleStatusChange(statusValue === 'in_progress' ? 'all' : 'in_progress')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${statusValue === 'in_progress' ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <Clock className={`mr-3 h-4 w-4 ${statusValue === 'in_progress' ? 'text-blue-600' : 'text-slate-400'}`} />
                  <span className={statusValue === 'in_progress' ? 'font-medium text-blue-700' : 'text-slate-700'}>İşlemde</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleStatusChange(statusValue === 'waiting_customer' ? 'all' : 'waiting_customer')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${statusValue === 'waiting_customer' ? 'bg-yellow-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <Users className={`mr-3 h-4 w-4 ${statusValue === 'waiting_customer' ? 'text-yellow-600' : 'text-slate-400'}`} />
                  <span className={statusValue === 'waiting_customer' ? 'font-medium text-yellow-700' : 'text-slate-700'}>Müşteri Bekleniyor</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleStatusChange(statusValue === 'resolved' ? 'all' : 'resolved')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${statusValue === 'resolved' ? 'bg-purple-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <CheckCircle className={`mr-3 h-4 w-4 ${statusValue === 'resolved' ? 'text-purple-600' : 'text-slate-400'}`} />
                  <span className={statusValue === 'resolved' ? 'font-medium text-purple-700' : 'text-slate-700'}>Çözüldü</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleStatusChange(statusValue === 'closed' ? 'all' : 'closed')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${statusValue === 'closed' ? 'bg-gray-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <XCircle className={`mr-3 h-4 w-4 ${statusValue === 'closed' ? 'text-gray-600' : 'text-slate-400'}`} />
                  <span className={statusValue === 'closed' ? 'font-medium text-gray-700' : 'text-slate-700'}>Kapatıldı</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuSeparator className="my-2" />

              {/* Öncelik Filtreleri */}
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Öncelik
              </div>
              <DropdownMenuItem
                onClick={() => handlePriorityChange(priorityValue === 'low' ? 'all' : 'low')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${priorityValue === 'low' ? 'bg-gray-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <div className={`mr-3 w-2 h-2 rounded-full ${priorityValue === 'low' ? 'bg-gray-500' : 'bg-slate-400'}`} />
                  <span className={priorityValue === 'low' ? 'font-medium text-gray-700' : 'text-slate-700'}>Düşük</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handlePriorityChange(priorityValue === 'medium' ? 'all' : 'medium')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${priorityValue === 'medium' ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <div className={`mr-3 w-2 h-2 rounded-full ${priorityValue === 'medium' ? 'bg-blue-500' : 'bg-slate-400'}`} />
                  <span className={priorityValue === 'medium' ? 'font-medium text-blue-700' : 'text-slate-700'}>Orta</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handlePriorityChange(priorityValue === 'high' ? 'all' : 'high')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${priorityValue === 'high' ? 'bg-orange-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <div className={`mr-3 w-2 h-2 rounded-full ${priorityValue === 'high' ? 'bg-orange-500' : 'bg-slate-400'}`} />
                  <span className={priorityValue === 'high' ? 'font-medium text-orange-700' : 'text-slate-700'}>Yüksek</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handlePriorityChange(priorityValue === 'urgent' ? 'all' : 'urgent')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${priorityValue === 'urgent' ? 'bg-red-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <div className={`mr-3 w-2 h-2 rounded-full ${priorityValue === 'urgent' ? 'bg-red-500 animate-pulse' : 'bg-slate-400'}`} />
                  <span className={priorityValue === 'urgent' ? 'font-medium text-red-700' : 'text-slate-700'}>Acil</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuSeparator className="my-2" />

              {/* Okunmamış Filtresi */}
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Mesaj Durumu
              </div>
              <DropdownMenuItem
                onClick={() => handleUnreadChange(!unreadValue)}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${unreadValue ? 'bg-red-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <div className={`mr-3 w-2 h-2 rounded-full ${unreadValue ? 'bg-red-500 animate-pulse' : 'bg-slate-400'}`} />
                  <span className={unreadValue ? 'font-medium text-red-700' : 'text-slate-700'}>Okunmamış Mesajlar</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Ayarlar Butonu */}
          <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="h-11 px-4 rounded-lg border-slate-200 hover:bg-slate-50"
              >
                <SettingsIcon className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <SettingsIcon className="h-5 w-5 text-emerald-600" />
                  Destek Ayarları
                </DialogTitle>
                <DialogDescription>
                  Destek sistemi ayarlarını buradan yönetebilirsiniz.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6 py-4">
                {/* Anlık Chat Toggle */}
                <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-slate-50">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${liveChatEnabled ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                      <MessageSquare className={`h-5 w-5 ${liveChatEnabled ? 'text-emerald-600' : 'text-slate-500'}`} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="live-chat" className="text-sm font-medium">
                        Anlık Mesajlaşma
                      </Label>
                      <p className="text-xs text-slate-500">
                        {liveChatEnabled
                          ? 'Müşteriler ticket içinde anlık mesaj yazabilir.'
                          : 'Müşteriler sadece ticket oluşturabilir, yanıt bekler.'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="live-chat"
                    checked={liveChatEnabled}
                    onCheckedChange={handleLiveChatToggle}
                  />
                </div>

                {/* Açıklama */}
                <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <strong className="text-amber-700">Not:</strong> Anlık mesajlaşma kapatıldığında, müşteriler ticket oluşturduktan sonra
                  mesaj yazamaz. Sadece sizin yanıtınızı bekler. Kapalı ticketları tekrar açıp mesaj gönderebilirler.
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Aktif filtre belirteçleri */}
        {isAnyFilterActive && (
          <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
            <span className="text-xs font-medium text-slate-600 flex items-center">
              Aktif Filtreler:
            </span>

            {statusValue === 'open' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-800 border-0 hover:bg-green-200">
                <AlertCircle className="h-3 w-3" />
                Açık
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-green-600"
                  onClick={() => onStatusChange('all')}
                />
              </Badge>
            )}

            {statusValue === 'in_progress' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-800 border-0 hover:bg-blue-200">
                <Clock className="h-3 w-3" />
                İşlemde
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-blue-600"
                  onClick={() => onStatusChange('all')}
                />
              </Badge>
            )}

            {statusValue === 'waiting_customer' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-yellow-100 text-yellow-800 border-0 hover:bg-yellow-200">
                <Users className="h-3 w-3" />
                Müşteri Bekleniyor
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-yellow-600"
                  onClick={() => onStatusChange('all')}
                />
              </Badge>
            )}

            {statusValue === 'resolved' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-800 border-0 hover:bg-purple-200">
                <CheckCircle className="h-3 w-3" />
                Çözüldü
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-purple-600"
                  onClick={() => onStatusChange('all')}
                />
              </Badge>
            )}

            {statusValue === 'closed' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-800 border-0 hover:bg-gray-200">
                <XCircle className="h-3 w-3" />
                Kapatıldı
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-gray-600"
                  onClick={() => onStatusChange('all')}
                />
              </Badge>
            )}

            {priorityValue !== 'all' && (
              <Badge className={`flex items-center gap-1.5 px-3 py-1 border-0 ${
                priorityValue === 'low' ? 'bg-gray-100 text-gray-800 hover:bg-gray-200' :
                priorityValue === 'medium' ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' :
                priorityValue === 'high' ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' :
                'bg-red-100 text-red-800 hover:bg-red-200'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  priorityValue === 'low' ? 'bg-gray-500' :
                  priorityValue === 'medium' ? 'bg-blue-500' :
                  priorityValue === 'high' ? 'bg-orange-500' :
                  'bg-red-500 animate-pulse'
                }`} />
                {priorityValue === 'low' ? 'Düşük' : priorityValue === 'medium' ? 'Orta' : priorityValue === 'high' ? 'Yüksek' : 'Acil'}
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer"
                  onClick={() => onPriorityChange('all')}
                />
              </Badge>
            )}

            {unreadValue && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-800 border-0 hover:bg-red-200">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                Okunmamış Mesajlar
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-red-600"
                  onClick={() => onUnreadChange(false)}
                />
              </Badge>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="h-7 px-3 text-xs text-slate-600 hover:text-slate-800 hover:bg-slate-200"
            >
              Tümünü Temizle
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
